<script setup>
import { Button } from '@/components/ui/button';
import { usefetchHealthInsurances } from '@/hooks/use-health-insurances';
import { useMedicalSchemesStore } from '@/store/use-medical-schemes-store';
import { ChevronRight } from 'lucide-vue-next';
import { computed, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';

// Get Store
const medicalSchemeStore = useMedicalSchemesStore();

// Get composable data
const { data, isLoading, error, refetch } = usefetchHealthInsurances()

const router = useRouter();
// State
const funders = ref([])
const searchQuery = ref('')

// Watch for changes in API data and update funders
watch(data, (newData) => {
  if (newData && Array.isArray(newData)) {
    funders.value = newData
  }
}, { immediate: true })

// Filtered funders based on search
const filteredFunders = computed(() => {
  if (!searchQuery.value.trim()) {
    return funders.value
  }

  const query = searchQuery.value.toLowerCase()
  return funders.value.filter(funder => {
    return (
      funder.name?.toLowerCase().includes(query) ||
      funder.scheme_code?.toLowerCase().includes(query) ||
      funder.category?.funder_type?.toLowerCase().includes(query) ||
      funder.funder_id?.toLowerCase().includes(query)
    )
  })
})

// Handle funder selection
const selectFunder = (funder) => {
  console.log('Selected funder:', funder);
  medicalSchemeStore.setMedicalScheme(funder);
  router.push(`/scheme-dashboard/${funder?.funder_id}`);
}

// Fetch data on component mount
onMounted(() => {
  refetch()
})
</script>

<template>
  <div class="w-full mx-auto">
    <!-- Header -->
    <div class="mb-8">
      <h1 class="text-3xl font-bold text-gray-900 mb-2">Medical Schemes</h1>
      <p class="text-gray-600">List of available medical funding schemes</p>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="flex justify-center items-center py-12">
      <div class="text-center">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-600 mx-auto mb-4"></div>
        <p class="text-gray-600">Loading schemes...</p>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-lg p-6">
      <div class="flex items-start gap-4">
        <div class="text-red-600 text-2xl">⚠️</div>
        <div>
          <h3 class="text-red-900 font-semibold mb-2">Error Loading Data</h3>
          <p class="text-red-700 mb-4">{{ error }}</p>
          <button
            @click="refetch"
            class="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition"
          >
            Try Again
          </button>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div v-else class="space-y-6">
      <!-- Search Bar -->
      <div>
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search by name, scheme code, or funder type..."
          class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      <!-- Results Count -->
      <div class="text-sm text-gray-600">
        Showing <span class="font-semibold">{{ filteredFunders.length }}</span> of <span class="font-semibold">{{ funders.length }}</span> schemes
      </div>

      <!-- Funders Grid -->
      <div v-if="filteredFunders.length > 0" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div
          v-for="funder in filteredFunders"
          :key="funder.funder_id"
          class="border border-skye-700 rounded-lg p-4 hover:shadow-lg transition-shadow bg-white"
        >
          <!-- Scheme Code Badge -->
          <div class="flex items-start justify-between">
            <div>
              <h3 class="text-lg font-semibold text-gray-900">{{ funder.name }}</h3>
              <span>{{ funder.category.funder_type  }}</span>
            </div>
            <span class="inline-flex text-xs items-center px-3 border border-yellow-700 rounded-full font-medium bg-slate-100 text-slate-800">
              {{ funder.scheme_code }}
            </span>
          </div>

          <!-- Action Button -->
           <div class="flex items-center justify-end">
            <Button
            class="cursor-pointer mt-4 px-4 py-2 bg-gradient-to-r from-[#255f7a] to-sky-700 text-white rounded-md hover:bg-blue-700 transition text-sm font-medium"
            @click="selectFunder(funder)"
          >
            Enter {{ funder?.name }} <ChevronRight />
          </Button>
           </div>
          
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="text-center py-12">
        <div class="text-gray-400 text-4xl mb-4">🔍</div>
        <p class="text-gray-600 text-lg">No schemes found matching "{{ searchQuery }}"</p>
      </div>
    </div>
  </div>
</template>

<style scoped>
</style>